'use client';

import React from 'react';
import AppLogo from '@/components/ui/AppLogo';
import { Instagram, Linkedin, Youtube, Twitter, ArrowUpRight } from 'lucide-react';

const footerLinks = {
  Work: [
    { label: 'Video Production', href: '#services' },
    { label: 'On-Site Shooting', href: '#services' },
    { label: 'AI-Enhanced', href: '#services' },
    { label: 'Post-Production', href: '#services' },
    { label: 'Full Archive', href: '#work' },
  ],
  Studio: [
    { label: 'Our Story', href: '#team' },
    { label: 'The Team', href: '#team' },
    { label: 'Client Tiers', href: '#tiers' },
    { label: 'Open Roles', href: '#' },
    { label: 'Press Kit', href: '#' },
  ],
  Contact: [
    { label: 'New Business', href: '#contact' },
    { label: 'hello@nasarc.studio', href: 'mailto:hello@nasarc.studio' },
    { label: 'Los Angeles', href: '#' },
    { label: 'London', href: '#' },
    { label: 'Dubai', href: '#' },
  ],
};

const socials = [
  { icon: Instagram, label: 'Instagram', href: '#' },
  { icon: Linkedin, label: 'LinkedIn', href: '#' },
  { icon: Youtube, label: 'YouTube', href: '#' },
  { icon: Twitter, label: 'X / Twitter', href: '#' },
];

export default function FooterSection() {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer className="bg-studio-900 border-t border-studio-700/50" role="contentinfo">
      {/* Main footer */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-12 2xl:px-16 py-16 xl:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-10 xl:gap-12">
          {/* Brand column */}
          <div className="xl:col-span-2">
            <button
              onClick={scrollToTop}
              className="flex items-center gap-3 mb-5 group focus:outline-none"
              aria-label="Back to top"
            >
              <AppLogo size={32} />
              <span className="font-display font-extrabold text-xl tracking-[0.12em] text-white uppercase group-hover:text-gold-400 transition-colors duration-200">
                NASARC
              </span>
            </button>

            <p className="text-studio-400 text-sm leading-relaxed mb-6 max-w-xs">
              A full-service creative studio making cinematic content for brands that refuse to be ignored. Video production, on-site shooting, AI-enhanced projects, and post-production — under one roof.
            </p>

            {/* Social links */}
            <div className="flex items-center gap-2">
              {socials?.map((social) => (
                <a
                  key={social?.label}
                  href={social?.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`NASARC on ${social?.label}`}
                  className="w-9 h-9 rounded-xl bg-studio-800/60 border border-studio-700/40 flex items-center justify-center text-studio-400 hover:text-white hover:border-studio-600/60 hover:bg-studio-700/60 transition-all duration-200"
                >
                  <social.icon size={15} />
                </a>
              ))}
            </div>

            {/* Awards note */}
            <div className="mt-6 flex items-center gap-2.5">
              <div className="w-1.5 h-1.5 rounded-full bg-gold-400" />
              <span className="text-studio-500 text-xs">14× award-winning studio · Est. 2019</span>
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks)?.map(([section, links]) => (
            <div key={section}>
              <p className="text-studio-300 text-xs font-semibold tracking-[0.15em] uppercase mb-4">
                {section}
              </p>
              <ul className="space-y-2.5" role="list">
                {links?.map((link) => (
                  <li key={link?.label}>
                    <a
                      href={link?.href}
                      onClick={(e) => {
                        if (link?.href?.startsWith('#')) {
                          e?.preventDefault();
                          const el = document.querySelector(link?.href);
                          if (el) el?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="group flex items-center gap-1 text-studio-500 text-sm hover:text-studio-200 transition-colors duration-200"
                    >
                      {link?.label}
                      {link?.href?.startsWith('mailto') && (
                        <ArrowUpRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-gold-400" />
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      {/* Bottom bar */}
      <div className="border-t border-studio-700/40">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-12 2xl:px-16 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-studio-600 text-xs">
            © {new Date()?.getFullYear()} NASARC Studio. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            {['Privacy Policy', 'Terms of Service', 'Cookie Preferences']?.map((item) => (
              <button
                key={item}
                className="text-studio-600 text-xs hover:text-studio-400 transition-colors duration-200 focus:outline-none"
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}